import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogScrollArea,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Globe, 
  Settings, 
  CheckCircle, 
  AlertCircle,
  Shield,
  Activity,
  Loader2
} from 'lucide-react';
import { BareAPISuccessModalContent } from './BareAPISuccessModal';
import { useTranslation } from 'react-i18next';
import { AdvancedSocketSelection } from '../AdvancedSocketSelection';
import { integrationRegistry } from '../../../integrations/registry';
import { SpaceSocket } from '../../../services/api';
import { getOrCreateDefaultSocket } from '../../../utils/socketUtils';
import { buildBindingPermissions } from '../shared/bindingPayload';
import { MemoryPermissionsFields } from '../shared/MemoryPermissionsFields';
import { RateLimitFields } from '../shared/RateLimitFields';

interface BareAPIIntegrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateIntegration: (integration: any) => Promise<any>;
  socket?: SpaceSocket;
  spaceId: string;
}

export default function BareAPIIntegrationModal({
  isOpen,
  onClose,
  onCreateIntegration,
  socket,
  spaceId
}: BareAPIIntegrationModalProps) {
  const { t } = useTranslation();
  const [integrationName, setIntegrationName] = useState('');
  const [description, setDescription] = useState('');
  const [responseFormat, setResponseFormat] = useState<'json'>('json');
  const [isCreating, setIsCreating] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdIntegration, setCreatedIntegration] = useState<any>(null);
  const [selectedSocket, setSelectedSocket] = useState<SpaceSocket | null>(socket || null);
  const [socketLoading, setSocketLoading] = useState(false);
  const [socketError, setSocketError] = useState<string | null>(null);

  // Get or create default socket when modal opens
  useEffect(() => {
    if (isOpen && !socket && spaceId) {
      const loadDefaultSocket = async () => {
        setSocketLoading(true);
        setSocketError(null);
        try {
          const defaultSocket = await getOrCreateDefaultSocket(spaceId, integrationRegistry.bare_api);
          setSelectedSocket(defaultSocket);
        } catch (error) {
          console.error('Failed to get or create default socket:', error);
          setSocketError(error instanceof Error ? error.message : 'Failed to load socket');
        } finally {
          setSocketLoading(false);
        }
      };
      loadDefaultSocket();
    } else if (socket) {
      setSelectedSocket(socket);
    }
  }, [isOpen, socket, spaceId]);

  // Permission states
  const [canRead, setCanRead] = useState(true);
  const [canWrite, setCanWrite] = useState(true);
  const [canSearch, setCanSearch] = useState(true);
  const [canDelete, setCanDelete] = useState(false);

  // Rate limiting
  const [requestsPerHour, setRequestsPerHour] = useState(1000);

  const socketTemplateId = selectedSocket?.socket_config?.template_id || 'api';
  const isCompatibleTemplate = ['api', 'mcp'].includes(socketTemplateId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!integrationName.trim() || !selectedSocket) return;

    if (!isCompatibleTemplate) {
      alert(t('bareAPIIntegration.errors.templateCompatibility', { ns: 'integrations' }) as string);
      return;
    }

    setIsCreating(true);
    try {
      const permissions = buildBindingPermissions({
        flags: {
          can_read: canRead,
          can_write: canWrite,
          can_search: canSearch,
          can_delete: canDelete,
        },
        categoriesComma: '',
        excludeTagsComma: '',
        minImportance: 1,
        requestsPerHour,
      });

      const auth_config = {
        response_format: responseFormat,
        description: description.trim() || undefined,
      };

      const integrationData = {
        integration_type: 'bare_api',
        integration_name: integrationName.trim(),
        permissions,
        auth_config,
        socket_id: selectedSocket.socket_id,
      };

      const createdIntegration = await onCreateIntegration(integrationData);
      
      setCreatedIntegration({
        integration_name: integrationData.integration_name,
        binding_id: createdIntegration.binding_id,
        auth_config: {
          ...integrationData.auth_config,
          api_key: createdIntegration.auth_config?.api_key || createdIntegration.access_token
        },
        permissions: integrationData.permissions,
        rate_limits: integrationData.permissions.rate_limits,
      });
      setShowSuccessModal(true);
      
    } catch (error: any) {
      console.error('Failed to create Bare API integration:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create Bare API integration. Please try again.';
      alert(errorMessage);
      throw error; // Re-throw to let parent handle if needed
    } finally {
      setIsCreating(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setCreatedIntegration(null);
    
    // Reset form
    setIntegrationName('');
    setDescription('');
    setResponseFormat('json');
    setCanRead(true);
    setCanWrite(true);
    setCanSearch(true);
    setCanDelete(false);
    setRequestsPerHour(1000);
    
    onClose();
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      if (showSuccessModal) {
        handleSuccessModalClose();
      } else {
        onClose();
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
      <DialogContent variant="scrollable" className="max-w-5xl bg-white">
        {showSuccessModal && createdIntegration ? (
          <BareAPISuccessModalContent
            integration={createdIntegration}
            onClose={handleSuccessModalClose}
            spaceName={selectedSocket?.socket_name || 'Memory Space'}
          />
        ) : (
          <>
            <DialogHeader className="flex-shrink-0 p-6 pb-4 pr-12">
              <DialogTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Globe className="w-6 h-6 text-blue-600" />
                Configure Bare API Integration
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                {selectedSocket ? (
                  <>Get direct REST API access to your Memara memory system for <span className="font-medium">{selectedSocket.socket_name}</span></>
                ) : (
                  'Setting up your integration...'
                )}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
              <DialogScrollArea className="space-y-6 px-6">
              {/* Basic Configuration */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-gray-700 text-sm font-medium">
                    Integration Name *
                  </label>
                  <Input
                    value={integrationName}
                    onChange={(e) => setIntegrationName(e.target.value)}
                    placeholder="e.g., My App API, Development API, Production API"
                    className="bg-white border-gray-300 text-gray-900"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-gray-700 text-sm font-medium">
                    Description
                  </label>
                  <Input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="What will this API integration be used for?"
                    className="bg-white border-gray-300 text-gray-900"
                  />
                </div>
              </div>

              <Tabs defaultValue="permissions" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="permissions">Permissions</TabsTrigger>
                  <TabsTrigger value="settings">Settings</TabsTrigger>
                  <TabsTrigger value="summary">Summary</TabsTrigger>
                </TabsList>

                {/* Permissions */}
                <TabsContent value="permissions" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Shield className="w-5 h-5" />
                        API Permissions
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-sm text-gray-600">
                        Configure what operations this API integration can perform on your memories.
                      </p>

                      <MemoryPermissionsFields
                        variant="embedded"
                        value={{
                          can_read: canRead,
                          can_write: canWrite,
                          can_search: canSearch,
                          can_delete: canDelete,
                        }}
                        onChange={(v) => {
                          setCanRead(v.can_read);
                          setCanWrite(v.can_write);
                          setCanSearch(v.can_search);
                          setCanDelete(v.can_delete);
                        }}
                      />

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <h4 className="font-medium text-blue-900 mb-2">Permission Guidelines</h4>
                        <div className="text-sm text-blue-800 space-y-1">
                          <p><strong>Development/Testing:</strong> Enable all permissions for full testing</p>
                          <p><strong>Read-only Apps:</strong> Read + Search for display-only applications</p>
                          <p><strong>Admin Tools:</strong> All permissions for management interfaces</p>
                          <p><strong>Automation:</strong> Write + Read for data processing workflows</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Settings */}
                <TabsContent value="settings" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Settings className="w-5 h-5" />
                        API Settings
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-gray-700 text-sm font-medium">
                          Response Format
                        </label>
                        <div className="p-3 bg-gray-50 rounded border">
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            {t('bareAPIIntegration.responseFormat.json', { ns: 'integrations' }) as string}
                          </Badge>
                          <p className="text-sm text-gray-600 mt-1">
                            {t('bareAPIIntegration.responseFormat.description', { ns: 'integrations' }) as string}
                          </p>
                        </div>
                      </div>

                      <RateLimitFields
                        requestsPerHour={requestsPerHour}
                        onRequestsPerHourChange={setRequestsPerHour}
                        min={1}
                        max={10000}
                      />

                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <h4 className="font-medium text-amber-900 mb-2">Security Notes</h4>
                        <div className="text-sm text-amber-800 space-y-1">
                          <p>• API key provides access to all memories in this socket</p>
                          <p>• Store your API key securely and never share it publicly</p>
                          <p>• Rate limits help prevent abuse and ensure system stability</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Summary */}
                <TabsContent value="summary" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Activity className="w-5 h-5" />
                        Integration Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-700 font-medium">Integration Name:</span>
                          <div className="text-gray-900">{integrationName || 'Not set'}</div>
                        </div>
                        <div>
                          <span className="text-gray-700 font-medium">Socket:</span>
                          <div className="text-gray-900">{socket?.socket_name}</div>
                        </div>
                        <div>
                          <span className="text-gray-700 font-medium">Permissions:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {canRead && <Badge variant="outline" className="text-xs">Read</Badge>}
                            {canWrite && <Badge variant="outline" className="text-xs">Write</Badge>}
                            {canSearch && <Badge variant="outline" className="text-xs">Search</Badge>}
                            {canDelete && <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">Delete</Badge>}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-700 font-medium">Rate Limit:</span>
                          <div className="text-gray-900">{requestsPerHour} requests/hour</div>
                        </div>
                      </div>

                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <h4 className="font-medium text-green-900 mb-2">What You'll Get</h4>
                        <div className="text-sm text-green-800 space-y-1">
                          <p>✅ Bearer token for API authentication</p>
                          <p>✅ REST endpoints for memory operations</p>
                          <p>{t('bareAPIIntegration.responseFormat.feature', { ns: 'integrations' }) as string}</p>
                          <p>✅ Rate limiting and security controls</p>
                          <p>✅ Usage examples and documentation</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>

              {/* Advanced Socket Selection */}
              {spaceId && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <AdvancedSocketSelection
                    integration={integrationRegistry.bare_api}
                    spaceId={spaceId}
                    currentSocket={selectedSocket}
                    onSocketChange={setSelectedSocket}
                    disabled={isCreating || socketLoading}
                  />
                  
                  {/* Socket Loading State */}
                  {socketLoading && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                        <p className="text-blue-800 text-sm">Setting up socket...</p>
                      </div>
                    </div>
                  )}

                  {/* Socket Error State */}
                  {socketError && !socketLoading && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
                        <div>
                          <p className="text-red-800 text-sm font-medium">Failed to load socket</p>
                          <p className="text-red-700 text-xs mt-1">{socketError}</p>
                          <p className="text-red-700 text-xs mt-1">You can create a socket manually in the advanced section above.</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Socket Info Display */}
                  {selectedSocket && !socketLoading && (
                    <>
                      {/* Template compatibility warning */}
                      {!isCompatibleTemplate && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                          <div className="flex items-start space-x-2">
                            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <h4 className="text-amber-900 font-medium text-sm">Template Compatibility Issue</h4>
                              <p className="text-amber-700 text-sm mt-1">
                                This socket uses the <strong>{socketTemplateId}</strong> template, which is not optimized for API integrations.
                                Consider creating a new socket with the <strong>API</strong> template for the best experience.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {isCompatibleTemplate && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-3">
                          <div className="flex items-start space-x-2">
                            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <h4 className="text-green-900 font-medium text-sm">Perfect Template Match</h4>
                              <p className="text-green-700 text-sm mt-1">
                                This socket uses the {socketTemplateId.toUpperCase()} template, which is optimized for API integrations.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              </DialogScrollArea>
              <DialogFooter variant="scrollable" className="gap-3 px-6 pb-6">
                <Button type="button" variant="outline" onClick={onClose} disabled={isCreating}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={isCreating || !integrationName.trim() || !selectedSocket || socketLoading}
                >
                  {isCreating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating Integration...
                    </>
                  ) : (
                    'Create API Integration'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
} 